/**
 * <aura-buy-box> — progressive enhancement for the bundle picker + add-to-cart form.
 * Works with zero JS too: the radios are a real product form, so a normal POST to
 * /cart/add still works if this script fails to load. With JS, it additionally:
 *  - swaps the headline price/compare-price when a bundle card is selected
 *  - pre-selects a variant's assigned gallery photo when that bundle is chosen,
 *    without permanently locking out manual thumbnail clicks afterward
 *  - submits via the Cart AJAX API and shows an inline "Added" state
 */
class AuraBuyBox extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-aura-buy-form]');
    this.radios = Array.from(this.querySelectorAll('input[name="id"]'));
    this.priceEl = this.querySelector('[data-aura-current-price]');
    this.compareEl = this.querySelector('[data-aura-compare-price]');
    this.saveEl = this.querySelector('[data-aura-save-badge]');
    this.submitLabel = this.querySelector('[data-aura-submit-label]');

    this.radios.forEach((radio) =>
      radio.addEventListener('change', () => {
        this.syncPrice(radio);
        this.syncGalleryImage(radio);
      })
    );

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }
  }

  syncPrice(radio) {
    const card = radio.closest('[data-aura-price]');
    if (!card) return;
    const price = card.getAttribute('data-aura-price');
    const compare = card.getAttribute('data-aura-compare');
    const save = card.getAttribute('data-aura-save');

    if (this.priceEl && price) this.priceEl.textContent = price;
    if (this.compareEl) {
      if (compare) {
        this.compareEl.textContent = compare;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }
    if (this.saveEl) {
      if (save) {
        this.saveEl.textContent = save;
        this.saveEl.hidden = false;
      } else {
        this.saveEl.hidden = true;
      }
    }
    if (this.submitLabel && price) {
      this.submitLabel.textContent = this.submitLabel.dataset.template.replace('{price}', price);
    }
  }

  syncGalleryImage(radio) {
    // Only pre-select a photo the moment the variant changes — this does not
    // re-run on its own, so a shopper clicking a different thumbnail afterward
    // stays in full control of the main image.
    const targetId = radio.dataset.auraGalleryTarget;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (target) target.checked = true;
  }

  async handleSubmit(event) {
    if (!window.fetch) return; // let the native form submission happen
    event.preventDefault();

    const button = this.form.querySelector('[type="submit"]');
    const formData = new FormData(this.form);

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) throw new Error('Add to cart failed');

      this.dispatchEvent(new CustomEvent('aura:added-to-cart', { bubbles: true }));

      if (button) {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        window.setTimeout(() => button.classList.remove('is-added'), 2200);
      }
    } catch (error) {
      // Fall back to a normal form submission so the shopper's action still completes.
      this.form.submit();
    } finally {
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('aura-buy-box')) {
  customElements.define('aura-buy-box', AuraBuyBox);
}
